import {
  applyRemoteColumnInsert,
  applyRemoteColumnUpdate,
  syncColumnsFromSupabase
} from '@/store/mutations'
import { useUserStore } from '@/store/user'
import type { Column } from '@/types'
import {
  createClient,
  type Session,
  FunctionsFetchError,
  FunctionsHttpError,
  FunctionsRelayError
} from '@supabase/supabase-js'
import { ref, computed, watch } from 'vue'
import { useOnline } from '@vueuse/core'
import type { Database } from '@/types/database.types'
import db from '@/plugins/database'
import { useNotify } from '@/composables/useNotify'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabasePublishableKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY

export const session = ref<Session | null>(null)
export const isLoggedIn = computed(() => session.value !== null)

export const supabase = createClient<Database>(
  supabaseUrl,
  supabasePublishableKey,
  {
    auth: {
      autoRefreshToken: false,
      persistSession: true,
      detectSessionInUrl: false
    }
  }
)

export async function setSupabaseSession(supabaseSession: {
  access_token: string
  refresh_token: string
}) {
  const { data, error } = await supabase.auth.setSession(supabaseSession)
  if (error) throw error
  session.value = data.session
}

export async function signOut() {
  session.value = null
  await supabase.auth.signOut({ scope: 'local' })
}

export async function loadColumns() {
  const { data, error } = await supabase
    .from('columns')
    .select('id, order, type, params, highlight')
    .order('order', { ascending: true })
  if (error) return console.error('[supabase] loadColumns failed', error)
  if (data) return syncColumnsFromSupabase(data as Column[])
}

function subscribeToColumns(userId: string) {
  // Broadcast DB (topic privé `columns:{user_id}`) plutôt que postgres_changes ; le garde dequal de mutations.ts absorbe l'écho de l'émetteur.
  supabase
    .channel(`columns:${userId}`, { config: { private: true } })
    .on('broadcast', { event: '*' }, async payload => {
      try {
        const {
          type,
          new: newRecord,
          old: oldRecord
        } = payload as {
          type: string
          new?: Column
          old?: { id: string }
        }
        if (type === 'INSERT' && newRecord) {
          await applyRemoteColumnInsert(newRecord)
        } else if (type === 'UPDATE' && newRecord) {
          await applyRemoteColumnUpdate(newRecord)
        } else if (type === 'DELETE' && oldRecord?.id) {
          await db.columns.delete(oldRecord.id)
        }
      } catch (e) {
        console.error('columns-sync: failed to apply realtime change', e)
        useNotify().error(
          e instanceof Error
            ? e.message
            : 'Erreur de synchronisation des colonnes'
        )
      }
    })
    .subscribe()
}

const isOnline = useOnline()
watch(isOnline, online => {
  if (online && session.value) loadColumns()
})

supabase.auth.onAuthStateChange((event, currentSession) => {
  session.value = currentSession
  if (event === 'SIGNED_IN' && currentSession) {
    loadColumns()
    const userId = currentSession.user.id
    void supabase.removeChannel(supabase.channel(`columns:${userId}`))
    subscribeToColumns(userId)
    useUserStore().loadFromSession(currentSession)
  } else if (
    (event === 'INITIAL_SESSION' || event === 'TOKEN_REFRESHED') &&
    currentSession
  ) {
    useUserStore().loadFromSession(currentSession)
  } else if (event === 'SIGNED_OUT') {
    void supabase.removeAllChannels()
    useUserStore().clear()
  }
})

export async function handleFunctionsError(error: unknown) {
  if (error instanceof FunctionsHttpError) {
    let errorMessage: string | undefined
    try {
      errorMessage = (await error.context.json()).error
    } catch {
      // Body non-JSON (ex. gateway 503 quand la edge function ne tourne pas)
    }
    throw new Error(errorMessage || error.message)
  } else if (error instanceof FunctionsRelayError) {
    throw new Error(`Relay error: ${error.message}`)
  } else if (error instanceof FunctionsFetchError) {
    throw new Error(`Fetch error: ${error.message}`)
  }
}

export default supabase
